import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  TextField,
  InputAdornment,
  Stack,
  Paper,
  Fade,
  Zoom,
  Chip,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { CashRegister, CashRegisterSession } from 'src/types/cashRegister';
import { getLastClosedSession } from 'src/api/server/cashRegister';
import { useAuthContext } from 'src/auth/hooks';
import { getRequiredSessionAction, SessionValidationResult } from 'src/utils/cash-register';
import { fDateTime } from 'src/utils/format-time';
import { FCurrency } from 'src/utils/format-number';
import { getPaymentMethodIcon, getPaymentMethodLabel } from 'src/utils/payment-methods';

// ----------------------------------------------------------------------

interface CashRegisterSessionDialogProps {
  open: boolean;
  onClose: () => void;
  register: CashRegister | null;
  session: CashRegisterSession | null;
  onOpenSession: (amount: number) => Promise<void>;
  onCloseAndReopen?: (closingAmount: number, openingAmount: number) => Promise<void>;
  onCloseOnly?: (closingAmount: number) => Promise<void>;
  forceCloseOnly?: boolean;
  loading?: boolean;
  error?: string | null;
}

// ----------------------------------------------------------------------

// Helper: compute payment methods breakdown from session
function getPaymentMethodsBreakdown(session: CashRegisterSession | null) {
  if (!session) return [];

  // 1. Prefer explicit paymentMethods array from backend (most reliable)
  if (session.paymentMethods && session.paymentMethods.length > 0) {
    return session.paymentMethods
      .filter((pm) => Math.abs(pm.amount) > 0 || (pm.transactionCount || 0) > 0)
      .map((pm) => ({
        method: pm.method,
        amount: pm.amount,
        sales: pm.sales || 0,
        refunds: pm.refunds || 0,
        corrections: pm.corrections || 0,
        transactionCount: pm.transactionCount || 0,
      }));
  }

  // 2. Fallback: use totalSales if available (backend-computed)
  if (session.totalSales && session.totalSales > 0) {
    return [{ method: 'cash', amount: session.totalSales }];
  }

  // 3. Last resort: compute from transactions array
  const breakdown: Record<string, number> = {};
  const transactions = session.transactions || [];
  transactions.forEach((t) => {
    if (t.type === 'sale' || t.type === 'deposit') {
      const m = t.paymentMethod || 'cash';
      breakdown[m] = (breakdown[m] || 0) + t.amount;
    }
  });
  return Object.entries(breakdown)
    .filter(([, amount]) => amount > 0)
    .map(([method, amount]) => ({ method, amount }));
}

// ----------------------------------------------------------------------

export default function CashRegisterSessionDialog({
  open,
  onClose,
  register,
  session,
  onOpenSession,
  onCloseAndReopen,
  onCloseOnly,
  forceCloseOnly = false,
  loading = false,
  error = null,
}: CashRegisterSessionDialogProps) {
  const { t } = useLocales();
  const theme = useTheme();
  const { user } = useAuthContext();
  const [openingAmount, setOpeningAmount] = useState('');
  const [closingAmount, setClosingAmount] = useState('');
  const [step, setStep] = useState<'check' | 'close' | 'open'>('check');
  const [validation, setValidation] = useState<SessionValidationResult | null>(null);
  const [lastClosedSession, setLastClosedSession] = useState<CashRegisterSession | null>(null);

  // Compute payment methods breakdown
  const paymentMethods = useMemo(() => getPaymentMethodsBreakdown(session), [session]);

  // Compute expected amount: use backend value first, then calculate
  const calculateExpectedAmount = useCallback((sess: CashRegisterSession): number => {
    if (!sess) return 0;
    // Use backend-computed currentAmount if available
    if (sess.currentAmount !== undefined && sess.currentAmount !== null) {
      return sess.currentAmount;
    }
    // Use backend-computed expectedAmount if available
    if (sess.expectedAmount !== undefined && sess.expectedAmount !== null) {
      return sess.expectedAmount;
    }
    // Fallback: calculate from transactions
    const transactions = sess.transactions || [];
    const totalSales = transactions
      .filter((t) => t.type === 'sale' || t.type === 'deposit')
      .reduce((sum, t) => sum + t.amount, 0);
    const totalExpenses = transactions
      .filter((t) => t.type === 'withdrawal' || t.type === 'refund')
      .reduce((sum, t) => sum + t.amount, 0);
    return (sess.openingAmount || 0) + totalSales - totalExpenses;
  }, []);

  // Compute validation when session changes
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        const result = getRequiredSessionAction(session, t);
        setValidation(result);

        if (forceCloseOnly) {
          setStep('close');
        } else if (result.action === 'close_reopen') {
          setStep('close');
        } else if (result.action === 'open') {
          setStep('open');
        } else {
          setStep('check');
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [session, open, t, calculateExpectedAmount, forceCloseOnly]);

  // Fetch last closed session for default opening amount
  useEffect(() => {
    if (open && step === 'open' && user?.id) {
      getLastClosedSession(user.id)
        .then((response) => {
          if (response.success && response.data) {
            setLastClosedSession(response.data);
          }
        })
        .catch(() => {});
    }
  }, [open, step, register?.id, user?.id]);

  // Initialize amounts when dialog opens
  const [amountsInitialized, setAmountsInitialized] = useState(false);
  useEffect(() => {
    if (open && !amountsInitialized) {
      const defaultClosingAmount = session?.currentAmount !== undefined
        ? session.currentAmount.toString()
        : (session ? calculateExpectedAmount(session).toString() : '');
      setClosingAmount(defaultClosingAmount);
      setAmountsInitialized(true);
    }
  }, [open, amountsInitialized, session, calculateExpectedAmount]);

  useEffect(() => {
    if (!open) {
      setAmountsInitialized(false);
    }
  }, [open]);

  // Initialize opening amount when reaching 'open' step
  const [openingAmountInitialized, setOpeningAmountInitialized] = useState(false);
  useEffect(() => {
    if (open && step === 'open' && !openingAmountInitialized) {
      let defaultOpen = '';
      if (lastClosedSession?.closingAmount !== undefined) {
        defaultOpen = lastClosedSession.closingAmount.toString();
      } else if (session?.closingAmount !== undefined) {
        defaultOpen = session.closingAmount.toString();
      }
      setOpeningAmount(defaultOpen);
      setOpeningAmountInitialized(true);
    }
  }, [open, step, lastClosedSession, openingAmountInitialized, session?.closingAmount]);

  useEffect(() => {
    if (step === 'close') {
      setOpeningAmountInitialized(false);
    }
  }, [step]);

  const handleOpenSession = async () => {
    const amount = parseFloat(openingAmount);
    if (Number.isNaN(amount) || amount < 0) return;
    try {
      await onOpenSession(amount);
    } catch (err) {
      console.error('[CashRegisterSessionDialog] handleOpenSession error:', err);
    }
  };

  const handleCloseAndReopen = async () => {
    const closeAmt = parseFloat(closingAmount);
    const openAmt = parseFloat(openingAmount);
    if (Number.isNaN(closeAmt) || Number.isNaN(openAmt)) return;
    try {
      if (forceCloseOnly) {
        await onCloseOnly?.(closeAmt);
      } else {
        await onCloseAndReopen?.(closeAmt, openAmt);
      }
    } catch (err) {
      console.error('[CashRegisterSessionDialog] handleCloseAndReopen error:', err);
    }
  };

  const handleNextStep = () => {
    if (step === 'close') {
      setStep('open');
    }
  };

  // If session is valid, don't show anything
  if (validation?.canProceed) {
    return null;
  }

  // Compute expected amount for display
  const expectedAmount = session?.currentAmount !== undefined
    ? session.currentAmount
    : (session ? calculateExpectedAmount(session) : 0);
  const discrepancy = session && closingAmount
    ? parseFloat(closingAmount) - expectedAmount
    : 0;

  // Determine if this is a "close previous day" scenario
  const isCloseReopen = validation?.action === 'close_reopen' && !forceCloseOnly;

  // Theme colors for consistent styling
  const headerBg = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;
  const summaryBg = alpha(theme.palette.primary.main, 0.04);
  const summaryBorder = alpha(theme.palette.primary.main, 0.15);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header — uses theme primary color */}
      <Box
        sx={{
          p: 3,
          background: headerBg,
          color: 'white',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.common.white, 0.2),
              display: 'flex',
            }}
          >
            <Iconify
              icon={step === 'close' ? 'eva:clipboard-fill' : 'eva:unlock-fill'}
              width={32}
              height={32}
            />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {step === 'close'
                ? t('cash_register.session_dialog.close_title')
                : t('cash_register.session_dialog.open_title')}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {register?.name}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        {/* Alert message */}
        {validation?.message && (
          <Zoom in>
            <Alert
              severity={step === 'close' ? 'warning' : 'info'}
              sx={{ mb: 3, borderRadius: 2 }}
              icon={<Iconify icon={step === 'close' ? 'eva:alert-circle-fill' : 'eva:info-fill'} />}
            >
              {validation.message}
            </Alert>
          </Zoom>
        )}

        {/* Current session info — shown during close step */}
        {session && step === 'close' && (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              bgcolor: summaryBg,
              borderColor: summaryBorder,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary.dark">
              {t('cash_register.session_dialog.current_session_info')}
            </Typography>

            <Stack spacing={2}>
              {/* Opened at */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {t('cash_register.opened_at')}
                </Typography>
                <Chip
                  size="small"
                  color="primary"
                  label={fDateTime(session.openedAt)}
                  icon={<Iconify icon="eva:calendar-fill" />}
                />
              </Stack>

              {/* Opening Amount — with description */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('cash_register.opening_amount')}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {t('cash_register.session_dialog.opening_amount_hint') || 'Montant de départ en caisse'}
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="medium">
                  {FCurrency(session.openingAmount)}
                </Typography>
              </Stack>

              <Divider />

              {/* Payment methods breakdown */}
              {paymentMethods.length > 0 && (
                <>
                  <Typography variant="body2" fontWeight="medium" color="text.secondary">
                    {t('cash_register.session_dialog.payment_methods_detail')}
                  </Typography>
                  <Stack spacing={1}>
                    {paymentMethods.map((pm) => (
                      <Stack
                        key={pm.method}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Iconify
                            icon={getPaymentMethodIcon(pm.method)}
                            width={18}
                            color={theme.palette.text.secondary}
                          />
                          <Typography variant="body2" color="text.primary">
                            {getPaymentMethodLabel(pm.method, t)}
                          </Typography>
                        </Stack>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight="medium">
                            {FCurrency(pm.amount)}
                          </Typography>
                          {(pm.refunds > 0 || pm.corrections > 0) && (
                            <Typography variant="caption" color="text.secondary">
                              Enc. {FCurrency(pm.sales)} · Remb. {FCurrency(pm.refunds)} · Régul. {FCurrency(pm.corrections)}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                  <Divider />
                </>
              )}

              {/* Expected Amount — with description */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('cash_register.expected_amount')}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {t('cash_register.session_dialog.expected_amount_hint') || 'Montant théorique en caisse (ouverture + encaissements)'}
                  </Typography>
                </Box>
                <Typography variant="h6" color="primary.main" fontWeight="bold">
                  {FCurrency(expectedAmount)}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        )}

        {/* Closing form */}
        {step === 'close' && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              {t('cash_register.session_dialog.closing_amount')}
            </Typography>
            <TextField
              fullWidth
              autoFocus
              type="number"
              value={closingAmount}
              onChange={(e) => setClosingAmount(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:credit-card-fill" />
                    </InputAdornment>
                  ),
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            {discrepancy !== 0 && closingAmount && (
              <Alert
                severity={discrepancy > 0 ? 'success' : 'error'}
                sx={{ mt: 2, borderRadius: 2 }}
              >
                <Typography variant="body2">
                  {discrepancy > 0
                    ? t('cash_register.session_dialog.surplus', { amount: FCurrency(Math.abs(discrepancy)) })
                    : t('cash_register.session_dialog.deficit', { amount: FCurrency(Math.abs(discrepancy)) })}
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        {/* Opening form */}
        {step === 'open' && (
          <Box>
            {/* Pre-fill message */}
            {lastClosedSession?.closingAmount !== undefined && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                <Typography variant="body2">
                  {t('cash_register.session_dialog.opening_amount_pre_filled', {
                    amount: FCurrency(lastClosedSession.closingAmount),
                  })}
                </Typography>
              </Alert>
            )}

            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              {t('cash_register.session_dialog.opening_amount')}
            </Typography>
            <TextField
              fullWidth
              autoFocus
              type="number"
              value={openingAmount}
              onChange={(e) => setOpeningAmount(e.target.value)}
              placeholder={t('cash_register.session_dialog.amount_placeholder')}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:credit-card-fill" />
                    </InputAdornment>
                  ),
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />

            {register?.defaultOpeningAmount && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t('cash_register.session_dialog.default_amount')}: {FCurrency(register.defaultOpeningAmount)}
              </Typography>
            )}
          </Box>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ borderRadius: 2 }}
        >
          {t('common.cancel')}
        </Button>

        {step === 'close' ? (
          <LoadingButton
            variant="contained"
            color={forceCloseOnly ? 'error' : 'primary'}
            onClick={forceCloseOnly ? handleCloseAndReopen : handleNextStep}
            loading={loading}
            disabled={!closingAmount || parseFloat(closingAmount) < 0}
            sx={{ borderRadius: 2 }}
            startIcon={<Iconify icon={forceCloseOnly ? 'eva:close-circle-fill' : 'eva:arrow-forward-fill'} />}
          >
            {forceCloseOnly
              ? t('cash_register.session_dialog.close_only')
              : t('cash_register.session_dialog.close_and_continue')}
          </LoadingButton>
        ) : (
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={step === 'open' && isCloseReopen ? handleCloseAndReopen : handleOpenSession}
            loading={loading}
            disabled={!openingAmount || parseFloat(openingAmount) < 0}
            sx={{ borderRadius: 2 }}
            startIcon={<Iconify icon="eva:unlock-fill" />}
          >
            {step === 'open' && isCloseReopen
              ? t('cash_register.session_dialog.confirm_close_and_open')
              : t('cash_register.session_dialog.confirm_open')}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
